import * as React from 'react';
import {
    addNewInDayScrollRef,
    resetInDayScrollRef,
} from '../actions/calendarDayListBoundsMutators';
import { observer } from 'mobx-react-lite';
import { useAutoScroll } from 'owa-react-hooks/lib/useAutoScroll';
import { useCustomTimeout } from 'owa-react-hooks/lib/useCustomTimeout';
import SurfaceConstants from '../styles/SurfaceConstants';
import combineHandlers from '../utils/combineHandlers';
import { useSurfaceStore } from './StoreContextProvider';

export default observer(
    function InDayScrollContainer(
        {
            className,
            children,
            handlers,
        }: {
            className?: string;
            children?: React.ReactNode;
            handlers?: React.DOMAttributes<HTMLDivElement>;
        },
        ref: React.Ref<HTMLElement>
    ) {
        const scrollRef = React.useRef<HTMLElement>(null);
        React.useImperativeHandle(ref, () => scrollRef.current, []);

        const store = useSurfaceStore();

        React.useEffect(() => {
            resetInDayScrollRef(store);
            return () => resetInDayScrollRef(store);
        }, []);

        const [setRefTimer] = useCustomTimeout();
        const inDayScrollRefFunction = React.useCallback(
            (element: HTMLElement) => {
                scrollRef.current = element;
                if (element) {
                    setRefTimer(() => {
                        addNewInDayScrollRef(element, store);
                    }, 0);
                }
            },
            [store]
        );
        const autoScrollHandlers = useAutoScroll(SurfaceConstants.ScrollbarWidth);

        const handlersToAttach = combineHandlers(autoScrollHandlers, handlers);

        return (
            <div
                className={className}
                {...handlersToAttach}
                data-is-scrollable={true}
                ref={inDayScrollRefFunction}>
                {children}
            </div>
        );
    },
    { forwardRef: true }
);
